import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, Film, Lock, MessageSquare, Play, UploadCloud } from "lucide-react";

export function VideoReviewPage() {
  return (
    <article className="panel legal" style={{ maxWidth: 860, margin: "0 auto" }}>
      <header style={{ marginBottom: 28 }}>
        <span className="badge" style={{ marginBottom: 12 }}>
          <Film size={13} /> Frame-Accurate Video &amp; Audio Review · Free Frame.io Alternative
        </span>
        <h1 style={{ fontSize: "clamp(28px, 4.2vw, 42px)", fontWeight: 900, lineHeight: 1.12, margin: "0 0 12px" }}>
          Share Video &amp; Audio with Frame-Accurate Timestamp Comments (No Login)
        </h1>
        <p className="hint" style={{ fontSize: 16, margin: 0 }}>
          Deliver 4K MP4, ProRes MOV, and lossless WAV mixes with an interactive in-browser review player. Clients click any frame to leave timestamped feedback (`01:24.3`) — encrypted end-to-end with zero registration.
        </p>
      </header>

      {/* Interactive Demo Callout */}
      <div style={{ padding: 24, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 32 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20, alignItems: "center" }}>
          <div>
            <b style={{ fontSize: 18, color: "var(--lime)", display: "block", marginBottom: 6 }}>
              Send a Video or Audio Cut for Review
            </b>
            <p className="hint" style={{ margin: "0 0 16px" }}>
              Up to 5 GB free · Zero video re-encoding · Timestamped client comments bound to the playhead
            </p>
            <Link to="/" className="btn" style={{ textDecoration: "none" }}>
              <UploadCloud size={16} /> Upload Cut for Review <ArrowRight size={15} />
            </Link>
          </div>

          <div style={{ padding: 14, background: "var(--panel-solid)", border: "1px solid var(--border)", borderRadius: 12, fontSize: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <b>Sample Review Timeline</b>
              <span className="badge" style={{ padding: "2px 8px", fontSize: 11 }}>Playhead 01:24.3</span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div className="file" style={{ padding: "6px 10px" }}>
                <span><b style={{ color: "var(--lime)" }}>[00:18.5]</b> Director: Tighten transition before title card</span>
              </div>
              <div className="file" style={{ padding: "6px 10px" }}>
                <span><b style={{ color: "var(--lime)" }}>[01:24.3]</b> Colorist: Lift shadows on wide drone shot</span>
              </div>
              <div className="file" style={{ padding: "6px 10px" }}>
                <span><b style={{ color: "var(--lime)" }}>[02:41.0]</b> Producer: Bring voiceover up +2 dB</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <section style={{ marginBottom: 30 }}>
        <h2>Why Editors &amp; Producers Switch from Frame.io, Vimeo &amp; Wipster to LimeShare</h2>
        <p>
          Sending a draft cut to a client shouldn't require paying $19/user/month for Frame.io or waiting 20 minutes for Vimeo to
          transcode and compress your 10-bit color grade into a blurry proxy.
        </p>
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th style={{ color: "var(--lime)" }}>LimeShare Video Review</th>
              <th>Frame.io</th>
              <th>Vimeo / YouTube Unlisted</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>Free Size Limit</b></td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>5 GB per transfer (No signup)</td>
              <td>Paid subscription required</td>
              <td>Weekly quota caps</td>
            </tr>
            <tr>
              <td><b>Video Quality</b></td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>100% Original Master Bitstream</td>
              <td>Transcoded proxy</td>
              <td>Heavily recompressed</td>
            </tr>
            <tr>
              <td><b>Timestamped Comments</b></td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>Yes (Click timestamp to seek frame)</td>
              <td>Yes</td>
              <td>Basic comments</td>
            </tr>
            <tr>
              <td><b>Zero-Knowledge Security</b></td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>Yes (AES-256-GCM in browser RAM)</td>
              <td>No (Stored in plaintext on cloud)</td>
              <td>No (Scanned by AI &amp; Copyright bots)</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>How Frame-Accurate Zero-Knowledge Review Works</h2>
        <ol>
          <li><b>Upload your master cut:</b> Drop your MP4, MOV, or WAV stem (up to 5 GB free) onto LimeShare.</li>
          <li><b>Share the sealed link:</b> Your client opens the link in any desktop or mobile browser and clicks <b>Play</b>.</li>
          <li><b>Leave timestamped notes:</b> Whenever the client pauses or types a note, LimeShare binds the exact playhead timestamp (`MM:SS.f`). Clicking any timestamp jumps the player straight to that frame.</li>
          <li><b>Download the master:</b> Once approved, the client clicks <b>Save</b> to stream the uncompressed master file to disk.</li>
        </ol>
      </section>
    </article>
  );
}
